import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { Skeleton, Text } from '@musa.tech/design-system';
import { fmtWeightAxis, fmtWeightStr } from '@/utils/formatWeight';

interface MaterialBreakdown {
  name: string;
  co2: number;
}

interface GroupBreakdown {
  group: string;
  co2: number;
  materials: MaterialBreakdown[];
}

interface UnitData {
  unit: string;
  value: number;
  co2: number;
  breakdown?: GroupBreakdown[];
}

interface IAUnitRankingProps {
  data: UnitData[];
  isLoading?: boolean;
  error?: unknown;
}

const GRADIENT_COLORS = [
  'var(--primary-700)',
  'var(--primary-600)',
  'var(--primary-500)',
  'var(--primary-400)',
  'var(--primary-300)',
];

const truncate = (str: string, max = 16) =>
  str.length > max ? str.slice(0, max) + '…' : str;

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const co2 = payload[0]?.value as number;
  const breakdown: GroupBreakdown[] = payload[0]?.payload?.breakdown ?? [];

  return (
    <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-sm min-w-[180px]">
      <Text variant="label" size="small" className="text-foreground mb-1 block">{label}</Text>
      <Text variant="body" size="small" className="text-primary-700 mb-1">
        CO₂ evitado:{' '}
        <span className="font-semibold">{fmtWeightStr(co2)}</span>
      </Text>
      {breakdown.length > 0 && (
        <div className="mt-1.5 pt-1.5 border-t border-border space-y-1">
          {breakdown.map((grp) => (
            <div key={grp.group}>
              <div className="flex justify-between gap-6">
                <Text variant="label" size="small" className="text-foreground">{grp.group}</Text>
                <Text variant="label" size="small" className="text-foreground font-medium">{fmtWeightStr(grp.co2)}</Text>
              </div>
              {grp.materials.length > 1 && (
                <div className="pl-3 mt-0.5 space-y-0.5">
                  {grp.materials.map((m) => (
                    <div key={m.name} className="flex justify-between gap-6">
                      <Text variant="body" size="small" className="text-muted-foreground">{m.name}</Text>
                      <Text variant="body" size="small" className="text-muted-foreground">{fmtWeightStr(m.co2)}</Text>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const IAUnitRanking = ({ data, isLoading, error }: IAUnitRankingProps) => {
  if (isLoading) {
    return <Skeleton className="h-72 w-full rounded-xl" />;
  }

  const isEmpty = !data || data.length === 0;

  const displayData = [...(data ?? [])]
    .sort((a, b) => b.co2 - a.co2)
    .slice(0, 5)
    .map((d) => ({ ...d, label: truncate(d.unit) }));

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <Text variant="heading" size="large" className="text-foreground">
        Ranking por unidade
      </Text>
      <Text variant="body" size="small" className="text-muted-foreground mt-0.5 mb-4 block">
        Unidades com maior impacto ambiental positivo
      </Text>

      {error ? (
        <div className="flex flex-col items-center justify-center h-52 gap-2">
          <Text variant="body" size="small" className="text-error-500">
            Erro ao carregar dados: {String(error instanceof Error ? error.message : error)}
          </Text>
        </div>
      ) : isEmpty ? (
        <div className="flex flex-col items-center justify-center h-52 text-muted-foreground gap-2">
          <span className="text-3xl">🏆</span>
          <Text variant="body" size="small">Sem dados no período selecionado</Text>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={Math.max(180, displayData.length * 48)}>
          <BarChart
            data={displayData}
            layout="vertical"
            margin={{ top: 0, right: 10, left: 10, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--neutral-200)" />
            <XAxis
              type="number"
              tick={{ fontSize: 11, fill: 'var(--neutral-500)' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => fmtWeightAxis(v)}
            />
            <YAxis
              type="category"
              dataKey="label"
              tick={{ fontSize: 11, fill: 'var(--neutral-500)' }}
              tickLine={false}
              axisLine={false}
              width={110}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            <Bar dataKey="co2" radius={[0, 4, 4, 0]} maxBarSize={32}>
              {displayData.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={GRADIENT_COLORS[index] ?? GRADIENT_COLORS[GRADIENT_COLORS.length - 1]}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};
